import { type IntlShape } from 'react-intl'

import {
  MiraklReasonCodeV1V1,
  type UserOrderResponseWithFlagsSubOrdersInner,
  UserSubOrder1EnrichedStatusEnum,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { type PublicGlobalConfig } from '../../global-config'
import {
  userSubOrderAccessorGetTrackingUrl,
  userSubOrderAccessorIsDamagedProductClaimable,
  userSubOrderAccessorIsMarketplaceOrder,
  userSubOrderAccessorIsSelfService,
  userSubOrderAccessorIsSubOrderReturnable,
  userSubOrderGetIsSubOrderCancellablePerItem,
} from '../../order'
import { TENANT } from '../../tenant'
import {
  urlResolverGetAccountSubOrderDetailBySubOrderIdDamagedProducts,
  urlResolverGetAccountSubOrderDetailBySubOrderIdMissingProducts,
  urlResolverGetAccountSubOrderDetailBySubOrderIdProductsReturn,
  urlResolverGetOrderQuestionsMarketPlaceContactForm,
  urlResolverGetProductsCancellationPage,
  urlResolverGetRefundOrder,
  urlResolverGetReportOrderIncident,
} from '../../url-handling'

import { directContactFormSubjectPolicyGetByTranslatedSubject } from './directContactFormSubjectPolicySelectors'

type SelfServiceUrlParams = {
  config: PublicGlobalConfig
  isMiraklOrderReturnAvailable: boolean
  orderNumber: string
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2
}

const getMarketplaceSellerModalUrl = (
  { config, orderNumber, subOrder }: SelfServiceUrlParams,
  contactSubject: MiraklReasonCodeV1V1,
): string | null => {
  if (!subOrder.subOrderId) {
    return null
  }

  return urlResolverGetOrderQuestionsMarketPlaceContactForm(config, {
    contactSubject,
    orderId: orderNumber,
    subOrderId: subOrder.subOrderId,
  })
}

const getCancellationSelfServiceUrl = (
  params: SelfServiceUrlParams,
): string | null => {
  const { config, orderNumber, subOrder } = params

  if (!subOrder.subOrderId) {
    return null
  }

  if (userSubOrderAccessorIsMarketplaceOrder(subOrder)) {
    return getMarketplaceSellerModalUrl(
      params,
      MiraklReasonCodeV1V1.CANCEL_ORDER,
    )
  }

  if (!userSubOrderGetIsSubOrderCancellablePerItem(subOrder)) {
    return null
  }

  return urlResolverGetProductsCancellationPage(
    config,
    orderNumber,
    subOrder.subOrderId,
  )
}

const getReportOrderSelfServiceUrl = (
  params: SelfServiceUrlParams,
): string | null => {
  const { config, orderNumber, subOrder } = params

  if (!subOrder.subOrderId) {
    return null
  }

  if (userSubOrderAccessorIsMarketplaceOrder(subOrder)) {
    if (
      subOrder.enrichedStatus === UserSubOrder1EnrichedStatusEnum.Dispatched
    ) {
      return urlResolverGetReportOrderIncident(
        config,
        orderNumber,
        subOrder.subOrderId,
      )
    }

    return getMarketplaceSellerModalUrl(
      params,
      MiraklReasonCodeV1V1.ITEM_PROBLEM,
    )
  }

  if (userSubOrderAccessorIsDamagedProductClaimable(subOrder)) {
    return urlResolverGetAccountSubOrderDetailBySubOrderIdDamagedProducts({
      config,
      orderId: orderNumber,
      subOrderId: subOrder.subOrderId,
    })
  }

  return null
}

const getMissingOrIncorrectItemSelfServiceUrl = (
  params: SelfServiceUrlParams,
): string | null => {
  const { config, orderNumber, subOrder } = params

  if (!subOrder.subOrderId) {
    return null
  }

  if (userSubOrderAccessorIsMarketplaceOrder(subOrder)) {
    return getMarketplaceSellerModalUrl(
      params,
      MiraklReasonCodeV1V1.OTHER_QUESTION,
    )
  }

  if (userSubOrderAccessorIsSelfService(subOrder)) {
    return urlResolverGetAccountSubOrderDetailBySubOrderIdMissingProducts({
      config,
      orderId: orderNumber,
      subOrderId: subOrder.subOrderId,
    })
  }

  return null
}

const getReturnSelfServiceUrl = (
  params: SelfServiceUrlParams,
): string | null => {
  const { config, isMiraklOrderReturnAvailable, orderNumber, subOrder } = params

  if (!subOrder.subOrderId) {
    return null
  }

  if (userSubOrderAccessorIsMarketplaceOrder(subOrder)) {
    if (isMiraklOrderReturnAvailable) {
      return urlResolverGetRefundOrder(config, orderNumber, subOrder.subOrderId)
    }

    return getMarketplaceSellerModalUrl(
      params,
      MiraklReasonCodeV1V1.OTHER_QUESTION,
    )
  }

  if (userSubOrderAccessorIsSubOrderReturnable(subOrder)) {
    return urlResolverGetAccountSubOrderDetailBySubOrderIdProductsReturn(
      config,
      orderNumber,
      subOrder.subOrderId,
    )
  }

  return null
}

const getMarketplaceTrackingUrl = (
  params: SelfServiceUrlParams,
): string | null => {
  const { config, subOrder } = params
  const trackingUrl = userSubOrderAccessorGetTrackingUrl(
    config,
    subOrder.carrier,
  )

  if (trackingUrl) {
    return trackingUrl
  }

  return getMarketplaceSellerModalUrl(
    params,
    config.tenant === TENANT.AT
      ? MiraklReasonCodeV1V1.OTHER_QUESTION
      : MiraklReasonCodeV1V1.DELIVERY_QUESTION,
  )
}

const getSelfServiceUrl = ({
  config,
  formSubject,
  intl,
  isMiraklOrderReturnAvailable,
  orderNumber,
  subOrder,
}: SelfServiceUrlParams & {
  formSubject: string
  intl: IntlShape
}): string | null => {
  const selfServiceUrlParams = {
    config,
    isMiraklOrderReturnAvailable,
    orderNumber,
    subOrder,
  }
  const isMarketplaceOrder = userSubOrderAccessorIsMarketplaceOrder(subOrder)
  const { policy } = directContactFormSubjectPolicyGetByTranslatedSubject({
    intl,
    subject: formSubject,
  })

  switch (policy.selfServiceRouteStrategy) {
    case 'changeOrder': {
      return isMarketplaceOrder
        ? getMarketplaceSellerModalUrl(
            selfServiceUrlParams,
            MiraklReasonCodeV1V1.OTHER_QUESTION,
          )
        : null
    }

    case 'documents': {
      return isMarketplaceOrder
        ? getMarketplaceSellerModalUrl(
            selfServiceUrlParams,
            MiraklReasonCodeV1V1.REQUEST_INVOICE,
          )
        : null
    }

    case 'orderCancellation': {
      return getCancellationSelfServiceUrl(selfServiceUrlParams)
    }

    case 'orderMissingOrIncorrectItem': {
      return getMissingOrIncorrectItemSelfServiceUrl(selfServiceUrlParams)
    }

    case 'reportOrder': {
      return getReportOrderSelfServiceUrl(selfServiceUrlParams)
    }

    case 'returnOrder': {
      return getReturnSelfServiceUrl(selfServiceUrlParams)
    }

    case 'tracking': {
      return isMarketplaceOrder
        ? getMarketplaceTrackingUrl(selfServiceUrlParams)
        : null
    }

    default: {
      return null
    }
  }
}

export const directContactFormOrderSelectionAccessorGetSelfServiceUrl = (
  params: SelfServiceUrlParams & { formSubject: string; intl: IntlShape },
): string | null => getSelfServiceUrl(params)
